import { useMemo } from "react"
import { useSuspenseQuery, QueryFunctionContext } from "@tanstack/react-query"
import { useParams } from "react-router-dom"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { Config, ConfigAPIResponse } from "../types"
import { findFund } from "../utils/funds"
import { IncompleteDonationFormConfigResponseError } from "../errors"

function parseConfig(response: ConfigAPIResponse): Config {
  const funds = response.data.attributes.funds
  const default_fund_id = response.data.relationships.default_fund.data.id
  const default_fund = findFund(funds, default_fund_id)

  if (!default_fund) {
    throw new Error(`Default fund with ID ${default_fund_id} not found`)
  }

  return {
    ...response.data.attributes,
    funds,
    default_fund,
  }
}

export function useParsedConfig(): Config {
  const { fundSlug } = useParams<{ fundSlug?: string }>()

  const searchParams = useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    if (fundSlug) {
      params.set("direct_link_fund_slug", fundSlug)
    }
    return params.toString()
  }, [fundSlug])

  const { data: configResponse } = useSuspenseQuery<ConfigAPIResponse>({
    queryKey: [`/giving/v2/donation_form_config?${searchParams}`],
    queryFn: async ({ queryKey }: QueryFunctionContext) => {
      const [path] = queryKey
      const response = await sessionApiClient.get(path)
      if (!response.data) {
        throw new IncompleteDonationFormConfigResponseError({ response })
      }
      return response
    },
    refetchOnWindowFocus: false,
  })

  return useMemo(() => parseConfig(configResponse), [configResponse])
}
